/**
 * Collections come from two different ETL paths:
 *
 *  - legacy: `custom_collections` + `smart_collections`, two REST resources;
 *  - new: a single `collections` resource, one GraphQL bulk snapshot.
 *
 * The backend triggers only the new one now, for every merchant, but a store
 * keeps reading the legacy pair until its first `collections` period has
 * actually run, so both readings stay live through the transition.
 *
 * Every surface that shows a collections count has to read whichever one the
 * store is actually on, so the rule lives here once. Mirrors atlas
 * `app/onboarding/api/importProgress.ts`.
 */

export interface CollectionsCount {
  resource: string;
  modelCount: number;
  apiCount: number;
}

/** Nothing to import (apiCount 0) counts as read in, same as the callers' own done checks. */
function isResourceIn(count?: CollectionsCount | null): boolean {
  if (!count || !count.resource) return false;
  return count.apiCount === 0 || count.modelCount >= count.apiCount;
}

/**
 * The collections reading to display: the GraphQL resource once it has read in,
 * otherwise the legacy pair summed.
 *
 * A store still on legacy data can never read in on `collections` — REST omits
 * collections built on the 2026-07 model, so its apiCount (GraphQL, e.g. 272)
 * stays above
 * the model count the REST jobs produced (e.g. 247) — which is why the sum is
 * still the fallback rather than a dead branch.
 */
export function resolveCollectionsCount(
  collections?: CollectionsCount | null,
  custom?: CollectionsCount | null,
  smart?: CollectionsCount | null,
): CollectionsCount {
  if (isResourceIn(collections)) {
    return {
      resource: 'collections',
      apiCount: collections!.apiCount,
      modelCount: Math.min(collections!.modelCount, collections!.apiCount),
    };
  }
  const apiCount = (custom?.apiCount || 0) + (smart?.apiCount || 0);
  const modelCount = Math.min((custom?.modelCount || 0) + (smart?.modelCount || 0), apiCount);
  const loaded = !!(custom?.resource || smart?.resource || collections?.resource);
  return { resource: loaded ? 'collections' : '', modelCount, apiCount };
}

/** 0-100 for the collections quarter of the raw-import progress bars. */
export function collectionsProgress(count: CollectionsCount): number {
  return count.apiCount > 0 ? (count.modelCount / count.apiCount) * 100 : 100;
}

/**
 * Has this store switched to the GraphQL extract? Its legacy periods stay in the
 * DB after the backend stops creating new ones, so a store with a live
 * `collections` period must stop rendering the old pair — otherwise those two
 * rows sit at "out of date" forever.
 */
export function hasCollectionsGraphqlPeriod(
  etlStatuses: Array<{ resource?: string; etlType?: string; status?: string }>,
): boolean {
  return (etlStatuses || []).some(
    (item) => item.resource === 'collections'
      && item.etlType === 'etl_data_period'
      && item.status !== 'INACTIVE',
  );
}

export const LEGACY_COLLECTION_RESOURCES = ['custom_collections', 'smart_collections'];
